(() => {
  const ROOT_SELECTOR = '[data-olza-home]';

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    "'": '&#039;',
    '"': '&quot;'
  }[character]));

  const formatMoney = (cents, currency = 'MXN') => {
    try {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format((Number(cents) || 0) / 100) + ` ${currency}`;
    } catch (_error) {
      return `$${((Number(cents) || 0) / 100).toFixed(2)} ${currency}`;
    }
  };

  const initialize = (root) => {
    if (!root || root.dataset.olzaInitialized === 'true') return;
    root.dataset.olzaInitialized = 'true';

    const query = (selector) => root.querySelector(selector);
    const queryAll = (selector) => Array.from(root.querySelectorAll(selector));
    let toastTimer;

    const menuToggle = query('[data-menu-toggle]');
    const mobileMenu = query('[data-mobile-menu]');
    const searchPanel = query('[data-search-panel]');
    const cartDrawer = query('[data-cart-drawer]');
    const cartToggle = query('[data-cart-toggle]');
    const cartCount = query('[data-cart-count]');
    const cartItems = query('[data-cart-items]');
    const cartFooter = query('[data-cart-footer]');
    const cartTotal = query('[data-cart-total]');
    const cartError = query('[data-cart-error]');
    const toast = query('[data-toast]');

    const cartJsonUrl = root.dataset.cartJsonUrl;
    const cartAddUrl = root.dataset.cartAddUrl;
    const cartChangeUrl = root.dataset.cartChangeUrl;

    const showToast = (message) => {
      if (!toast) return;
      toast.textContent = message;
      toast.classList.add('is-visible');
      window.clearTimeout(toastTimer);
      toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 3400);
    };

    const updateBodyLock = () => {
      const overlayOpen = (searchPanel && !searchPanel.hidden) || (cartDrawer && !cartDrawer.hidden);
      document.body.classList.toggle('olza-is-locked', Boolean(overlayOpen));
    };

    const setMenu = (open) => {
      if (!menuToggle || !mobileMenu) return;
      menuToggle.setAttribute('aria-expanded', String(open));
      menuToggle.setAttribute('aria-label', open ? 'Cerrar menú' : 'Abrir menú');
      mobileMenu.hidden = !open;
    };

    const setSearch = (open) => {
      if (!searchPanel) return;
      if (open && cartDrawer) cartDrawer.hidden = true;
      searchPanel.hidden = !open;
      updateBodyLock();
      if (open) window.setTimeout(() => searchPanel.querySelector('input[type="search"]')?.focus(), 60);
    };

    const setCart = (open) => {
      if (!cartDrawer) return;
      if (open && searchPanel) searchPanel.hidden = true;
      cartDrawer.hidden = !open;
      updateBodyLock();
      if (open) refreshCart();
    };

    menuToggle?.addEventListener('click', () => {
      setMenu(menuToggle.getAttribute('aria-expanded') !== 'true');
    });

    mobileMenu?.querySelectorAll('a, button').forEach((item) => item.addEventListener('click', () => setMenu(false)));

    queryAll('[data-open-search]').forEach((button) => button.addEventListener('click', () => {
      setMenu(false);
      setSearch(true);
    }));
    queryAll('[data-close-search]').forEach((button) => button.addEventListener('click', () => setSearch(false)));
    cartToggle?.addEventListener('click', () => setCart(true));
    queryAll('[data-close-cart]').forEach((button) => button.addEventListener('click', () => setCart(false)));

    const renderEmptyCart = () => {
      if (!cartItems) return;
      cartItems.innerHTML = `
        <div class="empty-cart">
          <div class="empty-cart__icon" aria-hidden="true">○</div>
          <p>Tu carrito está listo para tu primer OLZA.</p>
          <button class="button button--secondary" type="button" data-close-cart data-scroll-to-products>Ver productos</button>
        </div>`;
      if (cartFooter) cartFooter.hidden = true;
    };

    const renderCart = (cart) => {
      const itemCount = Number(cart?.item_count || 0);
      if (cartCount) cartCount.textContent = String(itemCount);
      cartToggle?.setAttribute('aria-label', `Abrir carrito, ${itemCount} ${itemCount === 1 ? 'artículo' : 'artículos'}`);

      if (!cartItems || !itemCount) {
        renderEmptyCart();
        return;
      }

      const currency = cart.currency || 'MXN';
      cartItems.innerHTML = cart.items.map((item) => {
        const image = item.image
          ? `<img src="${escapeHtml(item.image)}&width=180" alt="" width="72" height="84" loading="lazy">`
          : '<div class="cart-line__image-placeholder" aria-hidden="true"></div>';
        const variant = item.variant_title && item.variant_title !== 'Default Title'
          ? `<p>${escapeHtml(item.variant_title)}</p>`
          : '';
        return `
          <article class="cart-line">
            ${image}
            <div>
              <h3>${escapeHtml(item.product_title)}</h3>
              ${variant}
              <p>${formatMoney(item.final_line_price, currency)}</p>
              <div class="cart-line__quantity" aria-label="Cantidad">
                <button type="button" data-cart-change data-line-key="${escapeHtml(item.key)}" data-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Reducir cantidad">−</button>
                <span>${item.quantity}</span>
                <button type="button" data-cart-change data-line-key="${escapeHtml(item.key)}" data-quantity="${item.quantity + 1}" aria-label="Aumentar cantidad">+</button>
              </div>
            </div>
            <button class="cart-line__remove" type="button" data-cart-change data-line-key="${escapeHtml(item.key)}" data-quantity="0" aria-label="Eliminar ${escapeHtml(item.product_title)}">×</button>
          </article>`;
      }).join('');

      if (cartTotal) cartTotal.textContent = formatMoney(cart.total_price, currency);
      if (cartFooter) cartFooter.hidden = false;
    };

    const showCartError = (message = '') => {
      if (!cartError) return;
      cartError.textContent = message;
      cartError.hidden = !message;
    };

    async function refreshCart() {
      if (!cartJsonUrl) return;
      try {
        const response = await fetch(cartJsonUrl, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        });
        if (!response.ok) throw new Error('No se pudo cargar el carrito.');
        renderCart(await response.json());
        showCartError('');
      } catch (error) {
        showCartError(error.message || 'No se pudo cargar el carrito.');
      }
    }

    const addToCart = async (button) => {
      if (!cartAddUrl || !button?.dataset.variantId || button.disabled) return;
      const originalText = button.querySelector('span')?.textContent || 'Agregar';
      button.classList.add('is-loading');
      button.disabled = true;
      if (button.querySelector('span')) button.querySelector('span').textContent = 'Agregando';

      try {
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            items: [{ id: Number(button.dataset.variantId), quantity: 1 }]
          })
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.description || payload.message || 'No se pudo agregar el producto.');
        await refreshCart();
        showToast(`${button.dataset.productTitle || 'El producto'} se agregó al carrito.`);
        setCart(true);
      } catch (error) {
        showToast(error.message || 'No se pudo agregar el producto. Inténtalo de nuevo.');
      } finally {
        button.classList.remove('is-loading');
        button.disabled = false;
        if (button.querySelector('span')) button.querySelector('span').textContent = originalText;
      }
    };

    const changeCart = async (button) => {
      if (!cartChangeUrl || !button?.dataset.lineKey) return;
      button.disabled = true;
      try {
        const response = await fetch(cartChangeUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            id: button.dataset.lineKey,
            quantity: Number(button.dataset.quantity || 0)
          })
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.description || payload.message || 'No se pudo actualizar el carrito.');
        renderCart(payload);
        showCartError('');
      } catch (error) {
        showCartError(error.message || 'No se pudo actualizar el carrito.');
      }
    };

    root.addEventListener('click', (event) => {
      const addButton = event.target.closest('[data-add-to-cart]');
      if (addButton && root.contains(addButton)) {
        event.preventDefault();
        addToCart(addButton);
        return;
      }

      const cartChangeButton = event.target.closest('[data-cart-change]');
      if (cartChangeButton && root.contains(cartChangeButton)) {
        event.preventDefault();
        changeCart(cartChangeButton);
        return;
      }

      const closeAndScroll = event.target.closest('[data-close-cart][data-scroll-to-products]');
      if (closeAndScroll && root.contains(closeAndScroll)) {
        setCart(false);
        window.setTimeout(() => query('#productos')?.scrollIntoView({ behavior: 'smooth' }), 80);
      }

      const sizeTab = event.target.closest('[data-size]');
      if (sizeTab && root.contains(sizeTab)) {
        const selected = sizeTab.dataset.size;
        queryAll('[data-size]').forEach((tab) => tab.setAttribute('aria-selected', String(tab.dataset.size === selected)));
        queryAll('[data-size-panel]').forEach((panel) => { panel.hidden = panel.dataset.sizePanel !== selected; });
      }

      const sizeLink = event.target.closest('[data-select-size]');
      if (sizeLink && root.contains(sizeLink)) {
        const selected = sizeLink.dataset.selectSize;
        query(`[data-size="${selected}"]`)?.click();
        query('#formatos')?.scrollIntoView({ behavior: 'smooth' });
      }

      const familyButton = event.target.closest('[data-select-family]');
      if (familyButton && root.contains(familyButton)) {
        const family = familyButton.dataset.selectFamily;
        query('#productos')?.scrollIntoView({ behavior: 'smooth' });
        queryAll('.product-card').forEach((card) => {
          card.classList.toggle('is-highlighted', card.dataset.family === family);
          window.setTimeout(() => card.classList.remove('is-highlighted'), 1900);
        });
      }
    });

    queryAll('[data-accordion-single]').forEach((group) => {
      group.querySelectorAll('.accordion-trigger').forEach((trigger) => {
        trigger.addEventListener('click', () => {
          const item = trigger.closest('.accordion-item');
          const panel = item?.querySelector('.accordion-panel');
          if (!item || !panel) return;
          const wasOpen = trigger.getAttribute('aria-expanded') === 'true';
          group.querySelectorAll('.accordion-item').forEach((otherItem) => {
            otherItem.classList.remove('is-open');
            otherItem.querySelector('.accordion-trigger')?.setAttribute('aria-expanded', 'false');
            const otherPanel = otherItem.querySelector('.accordion-panel');
            if (otherPanel) otherPanel.hidden = true;
          });
          if (!wasOpen) {
            item.classList.add('is-open');
            trigger.setAttribute('aria-expanded', 'true');
            panel.hidden = false;
          }
        });
      });
    });

    const revealItems = queryAll('.reveal');
    if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -40px' });
      revealItems.forEach((item) => observer.observe(item));
    } else {
      revealItems.forEach((item) => item.classList.add('is-visible'));
    }

    window.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      setMenu(false);
      setSearch(false);
      setCart(false);
    });

    refreshCart();
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initialize);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
